import { useTranslation } from 'react-i18next';
import { Grid, Typography } from '@mui/material';
import LabelValue from '../../LabelValue';
import { Provider } from '../../../data/models/provider.model';
import phoneUtils, { ProviderPhoneUse } from '../../../services/phoneUtils';
import addressUtils, {
  ProviderAddressUse,
} from '../../../services/addressUtils';
import { useMessagingMethod } from '../../../hooks/useMessagingMethod';
import AddressDisplay from '../../AddressDisplay';
import emailUtils, { ProviderEmailUse } from '../../../services/emailUtils';
import { useBooleanStatus } from '../../../hooks/useBooleanStatus';
import { FULLSTORY_MASK_CLASSNAME } from '../../../services/analyticsUtils';

interface ContactInfoProps {
  provider: Provider | null;
  titleStyle: string;
}

export default function ContactInfo(props: ContactInfoProps) {
  const { t } = useTranslation('common');

  const { provider, titleStyle } = props;

  const email = emailUtils.getProviderEmail(
    provider?.emailAddresses,
    ProviderEmailUse.Dispatch,
  );
  const address = addressUtils.getProviderAddress(
    provider?.addresses,
    ProviderAddressUse.Office,
  );
  const mainPhone = phoneUtils.getProviderPhone(
    provider?.phoneNumbers,
    ProviderPhoneUse.Main,
  );

  return (
    <>
      <Typography className={titleStyle}>{t('provider.contact')}</Typography>
      <Grid container spacing={2}>
        <Grid item sm={12} md={6}>
          <LabelValue
            label={t('provider.name')}
            value={provider?.transportationProviderName}
          />
        </Grid>
        <Grid item sm={12} md={6}>
          <LabelValue
            label={t('provider.dispatchEmail')}
            value={email?.emailAddress}
            className={FULLSTORY_MASK_CLASSNAME}
          />
        </Grid>
        <Grid item sm={12} md={6}>
          <LabelValue
            label={t('provider.contactFirst')}
            value={email?.contactFirstName}
            className={FULLSTORY_MASK_CLASSNAME}
          />
        </Grid>
        <Grid item sm={12} md={6}>
          <LabelValue
            label={t('provider.contactLast')}
            value={email?.contactLastName}
            className={FULLSTORY_MASK_CLASSNAME}
          />
        </Grid>
        <AddressDisplay address={address} showCounty={true} />
        <Grid item sm={12} md={6}>
          <LabelValue
            className={FULLSTORY_MASK_CLASSNAME}
            label={t('provider.mainPhone')}
            value={(() => {
              const phone = phoneUtils.getProviderPhone(
                provider?.phoneNumbers,
                ProviderPhoneUse.Main,
              );

              return phone
                ? `${phone.phoneNumber} ${
                    phone.phoneExtension
                      ? `${t('common.extension')}. ${phone.phoneExtension}`
                      : ''
                  }`
                : '';
            })()}
          />
        </Grid>
        <Grid item sm={12} md={6}>
          <LabelValue
            label={t('common.hasSMSConsent')}
            value={useBooleanStatus(mainPhone?.hasSMSConsent)}
          />
        </Grid>
        <Grid item sm={12} md={6}>
          <LabelValue
            className={FULLSTORY_MASK_CLASSNAME}
            label={t('provider.dispatchPhone')}
            value={(() => {
              const phone = phoneUtils.getProviderPhone(
                provider?.phoneNumbers,
                ProviderPhoneUse.Dispatch,
              );

              return phone
                ? `${phone.phoneNumber} ${
                    phone.phoneExtension
                      ? `${t('common.extension')}. ${phone.phoneExtension}`
                      : ''
                  }`
                : '';
            })()}
          />
        </Grid>
        <Grid item sm={12} md={6}>
          <LabelValue
            label={t('provider.messagingMethod')}
            value={useMessagingMethod(provider?.messagingMethodID)}
          />
        </Grid>
        <Grid item sm={12} md={6}>
          <LabelValue
            label={t('provider.driverMessagingMethod')}
            value={useMessagingMethod(provider?.defaultDriverMessagingMethodID)}
          />
        </Grid>
      </Grid>
    </>
  );
}
